/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Named Label Chip Component
 *
 * @file NamedLabelChip.jsx
 * @author Murhsid Ahamedh
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { PERFORMANCE_STATUS } from '../../common/constants';
import { getBackgroundColor, getTextColor } from '../../common/statusColorUtils';
import mixins from '../../assets/styles/mixins.scss';

const useStyles = makeStyles(() => ({
  chip: {
    display: 'inline-flex',
    alignItems: 'left',
    height: 20,
    borderRadius: '4px !important',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
    opacity: 1,
    transform: 'rotate(0deg)',
    '& .MuiChip-label': {
      paddingLeft: 8,
      paddingRight: 8,
      fontSize: '12px',
      lineHeight: '20px',
      fontWeight: 600
    }
  }
}));

const DEFAULT_COLOR = { backgroundColor: mixins.nameLabelBgColor, color: mixins.timeIconColor };
const PRESET_COLORS = {
  great: { backgroundColor: getBackgroundColor(PERFORMANCE_STATUS.GREAT), color: getTextColor(PERFORMANCE_STATUS.GREAT) },
  'needs review': { backgroundColor: getBackgroundColor(PERFORMANCE_STATUS.NEEDS_REVIEW), color: getTextColor(PERFORMANCE_STATUS.NEEDS_REVIEW) },
  okay: { backgroundColor: getBackgroundColor(PERFORMANCE_STATUS.OKAY), color: getTextColor(PERFORMANCE_STATUS.OKAY) },
  'needs work': { backgroundColor: getBackgroundColor(PERFORMANCE_STATUS.NEEDS_WORK), color: getTextColor(PERFORMANCE_STATUS.NEEDS_WORK) }
};

const normalize = (value) => (value || '').toString().trim().toLowerCase();

const resolveColors = (name, colorMap) => {
  const map = { ...PRESET_COLORS, ...(colorMap || {}) };
  const key = normalize(name);
  if (map[key]) return map[key];
  const compactKey = key.replace(/\s+/g, ' ');

  return map[compactKey] || DEFAULT_COLOR;
};

const NamedLabelChip = ({ name, count, colorMap, className, size, variant, width }) => {
  const classes = useStyles();
  const colors = resolveColors(name, colorMap);
  const label = typeof count === 'number' ? `${name} (${count})` : name;


  return (
    <Chip
      label={label}
      size={size}
      variant={variant}
      className={`${classes.chip} ${className}`}
      style={{
        ...colors,
        ...(width && { width })
      }}
    />
  );
};

NamedLabelChip.propTypes = {
  name: PropTypes.string.isRequired,
  count: PropTypes.number,
  colorMap: PropTypes.object,
  className: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  variant: PropTypes.oneOf(['default', 'outlined', 'filled']),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

NamedLabelChip.defaultProps = {
  count: undefined,
  colorMap: undefined,
  className: '',
  size: 'small',
  variant: 'filled',
  width: undefined
};

export default NamedLabelChip;

